import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "463",
  "slug": "what-is-an-estuary",
  "title": "What Is an Estuary? Where Rivers Meet the Sea",
  "excerpt": "Where a river meets the ocean, fresh and salt water mix to create one of the planet's richest ecosystems. Here is what an estuary is, how estuaries form, and why they matter.",
  "date": "2026-06-17",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1774384215615-63a9f663958b?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxyaXZlciUyMGVzdHVhcnklMjBhZXJpYWx8ZW58MXwwfHx8MTc4MTY4NDIyMnww&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "An estuary is a partly enclosed coastal body of water where a river's fresh water meets and mixes with salt water from the sea. Estuaries are among the most productive ecosystems on Earth, and many of the world's great cities grew up around them.",
  "content": [
    {
      "type": "paragraph",
      "text": "Some of the most important places on Earth are neither fully river nor fully sea. They are estuaries, the brackish meeting points where a river hands its water over to the ocean. If you have ever stood at the mouth of a great river and watched muddy water fan out into the blue, you have seen an estuary at work. They are easy to overlook on a map, yet they shape the climate, the wildlife, and the cities around them."
    },
    {
      "type": "heading",
      "text": "What Is an Estuary?"
    },
    {
      "type": "paragraph",
      "text": "An estuary is a partly enclosed body of coastal water where fresh water from a river mixes with salt water from the sea. That mixing creates brackish water, which is saltier than a river but less salty than the open ocean. Because the salinity shifts with the tides and the seasons, estuaries are some of the most dynamic environments anywhere on the coast, changing character twice a day as the tide rises and falls."
    },
    {
      "type": "paragraph",
      "text": "Estuaries go by many local names: bays, sounds, lagoons, harbors, inlets, and firths. Chesapeake Bay, San Francisco Bay, the Thames Estuary, and the Río de la Plata between Argentina and Uruguay are all estuaries, even though no two look quite alike."
    },
    {
      "type": "heading",
      "text": "How Estuaries Form"
    },
    {
      "type": "paragraph",
      "text": "Geographers usually sort estuaries into a few types based on how they came to be:"
    },
    {
      "type": "list",
      "items": [
        "Drowned river valleys, or coastal plain estuaries, where rising seas after the last ice age flooded the lower reaches of a river. Chesapeake Bay is a textbook example.",
        "Fjord estuaries, where the sea fills a deep, glacier-carved valley, common along the coasts of Norway, Chile, and British Columbia.",
        "Bar-built estuaries, where sandbars and barrier islands partly wall off a stretch of coast, trapping river water behind them.",
        "Tectonic estuaries, formed where movement of the Earth's crust drops a basin below sea level, as with San Francisco Bay."
      ]
    },
    {
      "type": "paragraph",
      "text": "The way the water layers also varies. In some estuaries lighter fresh water floats clearly on top of denser salt water, while in others the tides churn everything together into a uniform brackish mix. Those differences decide what can live there and how the channel behaves."
    },
    {
      "type": "heading",
      "text": "Why Estuaries Are So Alive"
    },
    {
      "type": "paragraph",
      "text": "Estuaries are often called the nurseries of the sea, and the nickname is earned. Rivers deliver a constant supply of nutrients, while the sheltered, shallow water is warmer and calmer than the open coast. The result is an explosion of life. Salt marshes, mangroves, mudflats, and seagrass beds support shellfish, fish, crabs, and enormous numbers of migrating birds. Many ocean fish spend their early lives in the safety of an estuary before heading out to sea, which is why healthy estuaries matter far beyond their own shores."
    },
    {
      "type": "paragraph",
      "text": "That richness has always drawn people too. Estuaries offer sheltered harbors, fresh water, fertile land, and abundant food, which is why London, New York, Buenos Aires, and countless other cities grew up at the mouths of rivers. A surprising share of the world's largest ports sit on estuaries for exactly these reasons."
    },
    {
      "type": "heading",
      "text": "Estuaries Under Pressure"
    },
    {
      "type": "paragraph",
      "text": "Because they sit exactly where rivers, oceans, and people meet, estuaries are also fragile. Pollution carried downstream, dredging for shipping, the draining of wetlands, and rising seas all put pressure on them. When the wetlands that fringe an estuary are lost, so is a natural buffer that once soaked up floods and storm surges. Many countries now protect their major estuaries precisely because so much depends on keeping that delicate fresh-and-salt balance intact."
    },
    {
      "type": "heading",
      "text": "Estuary or Delta?"
    },
    {
      "type": "paragraph",
      "text": "Estuaries are easy to confuse with deltas, since both form where a river meets the sea. The difference comes down to sediment and energy. A delta builds outward when a river dumps more sediment than the sea can carry away, creating new land in a fan of channels. An estuary forms where the sea reaches inland into a drowned or sheltered valley, and where tides and waves keep the mouth open rather than letting it fill in. Some great rivers, like the Nile, build deltas; others, like the Thames, open into estuaries."
    }
  ]
};
